/// <reference types="google.maps" />

import {
    Component,
    ViewChild,
    ElementRef,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import type { OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { GoogleMapService } from './google-map.service';
import type {
    Coordinate,
    MarkerConfig,
    RouteConfig,
    PolylineConfig,
    GoogleMapOptions,
    MarkerClickEvent,
    MapErrorEvent
} from './google-map.types';

/**
 * Reusable Google Map Component
 * 
 * Consolidates map functionality from:
 * - route5Verification.page.ts (route planning with letter markers)
 * - trackerLive.page.ts (live shuttle tracking with rotation)
 * - tracker3HistoricalRoutes.page.ts (historical route playback)
 * 
 * Features:
 * - AdvancedMarkerElement support for all marker types
 * - CSS transition-based shuttle animation (browser-optimized)
 * - Shuttle heading rotation (accepts degrees 0-360)
 * - Animation restart from current position on updates
 * - Pure SCSS styling (NO inline styles)
 * - Hybrid API: Inputs/Outputs + Imperative methods
 * 
 * Usage:
 * ```html
 * <app-google-map
 *   [dealershipCoordinate]="dealershipCoord"
 *   [initialZoom]="14"
 *   [mapId]="'SHUTTLE_MAP'"
 *   [showCenterButton]="true"
 *   (mapReady)="onMapReady()"
 *   (markerClick)="onMarkerClick($event)"
 *   (mapError)="onMapError($event)">
 * </app-google-map>
 * ```
 * 
 * Imperative API:
 * ```typescript
 * @ViewChild(GoogleMapComponent) mapComponent!: GoogleMapComponent;
 * 
 * this.mapComponent.addDealershipMarker(coord, 'Dealership Name', iconUrl);
 * this.mapComponent.addLetterMarker('A', coord, 'Passenger Name');
 * this.mapComponent.addShuttleMarker('shuttle1', coord, iconUrl, 'red', 180);
 * this.mapComponent.updateShuttle('shuttle1', newLat, newLng, newHeading);
 * this.mapComponent.centerMap(coord);
 * ```
 */
@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule]
})
export class GoogleMapComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('map') public mapRef!: ElementRef;

    // ================================================
    // Inputs - Configuration
    // ================================================

    @Input() public dealershipCoordinate?: Coordinate;
    @Input() public initialZoom = 14;
    @Input() public mapId = `SHUTTLE_MAP_${Date.now()}`;
    @Input() public showCenterButton = true;
    @Input() public centerButtonTitle = 'Center on Dealership';

    // ================================================
    // Outputs - Events
    // ================================================

    @Output() public mapReady = new EventEmitter<google.maps.Map>();
    @Output() public markerClick = new EventEmitter<MarkerClickEvent>();
    @Output() public mapError = new EventEmitter<MapErrorEvent>();

    // ================================================
    // Public Properties
    // ================================================

    public map!: google.maps.Map;
    public errorMessage = '';

    // ================================================
    // Private Properties
    // ================================================

    private centerCoordinate: Coordinate | null = null;

    // ================================================
    // Constructor & Lifecycle
    // ================================================

    constructor(private mapService: GoogleMapService) {}

    public ngOnInit(): void {
        // Validate required inputs
        if (!this.dealershipCoordinate) {
            // console.error('GoogleMapComponent: dealershipCoordinate is required');
        }
    }

    public async ngAfterViewInit(): Promise<void> {
        try {
            await this.initializeMap();
        } catch (err) {
            this.handleMapInitializationError(err);
        }
    }

    public ngOnDestroy(): void {
        this.mapService.cleanup();
    }

    // ================================================
    // Map Initialization
    // ================================================

    /**
     * Initialize the Google Map with Advanced Marker support
     */
    private async initializeMap(): Promise<void> {
        if (!this.dealershipCoordinate) {
            throw new Error('dealershipCoordinate is required');
        }

        if (!this.mapRef) {
            throw new Error('Map element reference not available');
        }

        // Use dealership as center if no custom center provided
        this.centerCoordinate = this.dealershipCoordinate;

        const mapOptions: google.maps.MapOptions = {
            center: new google.maps.LatLng(
                this.centerCoordinate.latitude,
                this.centerCoordinate.longitude
            ),
            zoom: this.initialZoom,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP
            },
            mapId: this.mapId, // Required for Advanced Markers
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            fullscreenControl: false,
            cameraControl: false,
            gestureHandling: 'greedy'
        };

        this.map = new google.maps.Map(this.mapRef.nativeElement, mapOptions);

        // Wait for map to be fully loaded
        await this.waitForMapReady();

        // Emit ready event
        this.mapReady.emit(this.map);
    }

    /**
     * Wait for map to be fully loaded and ready
     */
    private waitForMapReady(): Promise<void> {
        const promise = new Promise<void>((resolve) => {
            google.maps.event.addListenerOnce(this.map, 'idle', () => {
                resolve();
            });
        });

        return promise;
    }

    /**
     * Handle map initialization errors
     */
    private handleMapInitializationError(err: unknown): void {
        const message = err instanceof Error ? err.message : 'Failed to load map';
        this.errorMessage = message;

        this.mapError.emit({
            message: message,
            error: err
        });
    }

    // ================================================
    // UI Actions
    // ================================================

    /**
     * Center map on dealership (or last set center)
     */
    public onCenterClick(): void {
        if (this.centerCoordinate) {
            this.centerMap(this.centerCoordinate);
        }
    }

    // ================================================
    // Public API - Imperative Methods
    // ================================================

    /**
     * Add a dealership marker to the map
     */
    public addDealershipMarker(
        coordinate: Coordinate,
        dealershipName: string,
        iconUrl: string
    ): google.maps.marker.AdvancedMarkerElement {
        const config: MarkerConfig = {
            id: 'dealership',
            type: 'dealership',
            position: coordinate,
            title: dealershipName,
            zIndex: 100
        };

        return this.mapService.addMarker(this.map, config, iconUrl);
    }

    /**
     * Add a start marker to the map
     */
    public addStartMarker(
        coordinate: Coordinate,
        locationName: string,
        fullAddress: string,
        iconUrl: string
    ): google.maps.marker.AdvancedMarkerElement {
        const config: MarkerConfig = {
            id: 'start',
            type: 'start',
            position: coordinate,
            title: `Start: ${locationName}. ${fullAddress}`,
            label: `Start: ${locationName}`,
            zIndex: 10
        };

        return this.mapService.addMarker(this.map, config, iconUrl);
    }

    /**
     * Add an end marker to the map
     */
    public addEndMarker(
        coordinate: Coordinate,
        locationName: string,
        fullAddress: string,
        iconUrl: string
    ): google.maps.marker.AdvancedMarkerElement {
        const config: MarkerConfig = {
            id: 'end',
            type: 'end',
            position: coordinate,
            title: `End: ${locationName}. ${fullAddress}`,
            label: `End: ${locationName}`,
            zIndex: 10
        };

        return this.mapService.addMarker(this.map, config, iconUrl);
    }

    /**
     * Add a letter marker to the map (A, B, C, etc.)
     */
    public addLetterMarker(
        letter: string,
        coordinate: Coordinate,
        title: string
    ): google.maps.marker.AdvancedMarkerElement {
        const config: MarkerConfig = {
            id: `letter-${letter}`,
            type: 'letter',
            position: coordinate,
            title: title,
            label: letter,
            zIndex: 50
        };

        return this.mapService.addMarker(this.map, config);
    }

    /**
     * Add a shuttle marker to the map with initial heading
     */
    public addShuttleMarker(
        shuttleId: string,
        coordinate: Coordinate,
        iconUrl: string,
        color?: string,
        headingDegrees: number = 0
    ): google.maps.marker.AdvancedMarkerElement {
        const config: MarkerConfig = {
            id: shuttleId,
            type: 'shuttle',
            position: coordinate,
            title: `Shuttle ${shuttleId}`,
            color: color,
            zIndex: 75
        };

        const marker = this.mapService.addMarker(this.map, config, iconUrl);

        // Set initial rotation if heading provided
        if (headingDegrees !== 0) {
            this.mapService.rotateShuttleMarker(shuttleId, headingDegrees);
        }

        return marker;
    }

    /**
     * Update shuttle position with smooth CSS transition
     * Animation restarts from current position (not from previous target)
     * 
     * @param shuttleId - Unique shuttle identifier
     * @param latitude - New latitude
     * @param longitude - New longitude
     * @param headingDegrees - Direction shuttle is facing (0-360, where 0=North, 90=East)
     */
    public updateShuttle(
        shuttleId: string,
        latitude: number,
        longitude: number,
        headingDegrees: number
    ): void {
        this.mapService.updateShuttlePosition(shuttleId, latitude, longitude, headingDegrees);
    }

    /**
     * Remove a marker from the map
     */
    public removeMarker(markerId: string): void {
        this.mapService.removeMarker(markerId);
    }

    /**
     * Clear all markers from the map
     */
    public clearMarkers(): void {
        this.mapService.clearAllMarkers();
    }

    /**
     * Calculate and display a route between two points
     */
    public async calculateRoute(config: RouteConfig): Promise<google.maps.DirectionsResult> {
        try {
            const result = await this.mapService.calculateRoute(this.map, config);
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Route calculation failed';
            this.mapError.emit({ message, error: err });
            throw err;
        }
    }

    /**
     * Draw a polyline on the map (for historical routes)
     */
    public drawPolyline(config: PolylineConfig): google.maps.Polyline {
        return this.mapService.drawPolyline(this.map, config);
    }

    /**
     * Clear all routes from the map
     */
    public clearRoutes(): void {
        this.mapService.clearRoutes();
    }

    /**
     * Center the map on a specific coordinate
     */
    public centerMap(coordinate: Coordinate, zoom?: number): void {
        if (this.map) {
            const latLng = new google.maps.LatLng(coordinate.latitude, coordinate.longitude);
            this.map.setCenter(latLng);

            if (zoom !== undefined) {
                this.map.setZoom(zoom);
            }

            // Update center coordinate for "Center Me" button
            this.centerCoordinate = coordinate;
        }
    }

    /**
     * Get the underlying Google Map instance
     */
    public getMap(): google.maps.Map {
        return this.map;
    }
}
